import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, RefreshCw, ArrowLeft, ImagePlus } from "lucide-react";
import { useStore, API, CATEGORIES } from "@/context/StoreContext";
import { Logo } from "@/components/Navbar";
import { formatGs } from "@/lib/format";

const inputCls = "w-full h-11 px-4 rounded-xl border-2 border-ink bg-white font-medium focus:outline-none focus:ring-2 focus:ring-kpink/60 placeholder:text-ink/40 text-sm";
const EMPTY = { name: "", price: "", category: "utiles_escolares", image: "", tag: "" };

function formatError(e) {
  const d = e.response?.data?.detail;
  if (!d) return "Error de conexión";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return String(d);
}

export default function Admin() {
  const { user, setUser, fetchProducts } = useStore();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}` }));
      toast.success("Foto subida correctamente");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const loadAdmin = useCallback(async () => {
    try {
      const [p, o] = await Promise.all([
        axios.get(`${API}/admin/products`, { withCredentials: true }),
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
      ]);
      setProducts(p.data);
      setOrders(o.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (user) loadAdmin(); }, [user, loadAdmin]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      toast.success("¡Bienvenida, Kuki!");
    } catch (err) {
      setLoginError(formatError(err));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: Math.round(parseFloat(form.price) || 0), active: true };
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing}`, payload, { withCredentials: true });
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
        toast.success("Producto creado");
      }
      setForm(EMPTY);
      setEditing(null);
      loadAdmin();
      fetchProducts();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const toggleActive = async (p) => {
    await axios.put(`${API}/admin/products/${p.id}`, { ...p, active: !p.active }, { withCredentials: true });
    loadAdmin();
    fetchProducts();
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    await axios.delete(`${API}/admin/products/${p.id}`, { withCredentials: true });
    toast.success("Producto eliminado");
    loadAdmin();
    fetchProducts();
  };

  if (user === null) {
    return <div className="min-h-screen grid place-items-center bg-paper font-display font-bold text-xl">Cargando...</div>;
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-paper grain relative flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm bg-white border-2 border-ink rounded-3xl p-8 shadow-neo" data-testid="admin-login-form">
          <div className="flex justify-center mb-5"><Logo size={56} /></div>
          <h1 className="font-display font-extrabold text-2xl text-center">Panel Kuki</h1>
          <p className="text-center text-ink/60 text-sm font-medium mt-1 mb-6">Ingresá para gestionar tu catálogo</p>
          {loginError && (
            <div className="mb-4 p-3 rounded-xl border-2 border-kpink bg-kpink/10 text-kpink text-sm font-bold" data-testid="admin-login-error">
              {loginError}
            </div>
          )}
          <div className="space-y-3">
            <input type="email" required placeholder="Correo" value={loginForm.email}
              onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls} data-testid="admin-email-input" />
            <input type="password" required placeholder="Contraseña" value={loginForm.password}
              onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls} data-testid="admin-pin-input" />
            <button type="submit" data-testid="admin-login-btn"
              className="w-full h-12 rounded-xl border-2 border-ink bg-kyellow font-display font-bold shadow-neo-sm hover:-translate-y-0.5 transition-transform">
              Ingresar
            </button>
          </div>
          <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 text-sm font-bold text-ink/60 hover:text-ink transition-colors">
            <ArrowLeft size={15} /> Volver a la tienda
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-panel-container">
      <header className="border-b-2 border-ink bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={34} />
            <span className="font-display font-bold text-lg">Panel Kuki</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="h-10 px-4 inline-flex items-center gap-1.5 rounded-full border-2 border-ink bg-cream font-bold text-sm hover:-translate-y-0.5 transition-transform" data-testid="admin-back-store">
              <ArrowLeft size={15} /> Tienda
            </Link>
            <button onClick={loadAdmin} className="h-10 w-10 grid place-items-center rounded-full border-2 border-ink bg-white hover:bg-kcyan/40 transition-colors" data-testid="admin-refresh-btn">
              <RefreshCw size={16} />
            </button>
            <button onClick={logout} className="h-10 px-4 inline-flex items-center gap-1.5 rounded-full border-2 border-ink bg-kpink text-white font-bold text-sm hover:-translate-y-0.5 transition-transform" data-testid="admin-logout-btn">
              <LogOut size={15} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-12">
        <section className="bg-white border-2 border-ink rounded-3xl p-6 shadow-neo">
          <h2 className="font-display font-bold text-xl mb-5">
            {editing ? "Editar producto" : "Agregar producto"}
          </h2>
          <form onSubmit={saveProduct} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-product-form">
            <input required placeholder="Nombre del producto" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="admin-product-title-input" />
            <input required type="number" step="1" min="0" placeholder="Precio (₲)" value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className={inputCls} data-testid="admin-product-price-input" />
            <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls} data-testid="admin-product-category-select">
              {CATEGORIES.filter((c) => c.id !== "todos").map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadFile} data-testid="admin-product-image-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="h-11 px-4 inline-flex items-center gap-1.5 rounded-xl border-2 border-ink bg-kcyan font-bold text-sm shadow-neo-sm hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                data-testid="admin-product-upload-btn">
                <ImagePlus size={16} strokeWidth={2.5} /> {uploading ? "Subiendo..." : "Subir foto"}
              </button>
              {form.image && (
                <img src={form.image} alt="Vista previa" className="h-11 w-11 rounded-lg border-2 border-ink object-cover" data-testid="admin-product-image-preview" />
              )}
            </div>
            <input placeholder="Etiqueta (ej: Nuevo, Oferta)" value={form.tag}
              onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))} className={inputCls} data-testid="admin-product-tag-input" />
            <div className="flex gap-2">
              <button type="submit" data-testid="admin-add-product-btn"
                className="flex-1 h-11 inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-ink bg-kyellow font-bold text-sm shadow-neo-sm hover:-translate-y-0.5 transition-transform">
                <Plus size={16} strokeWidth={3} /> {editing ? "Guardar" : "Agregar"}
              </button>
              {editing && (
                <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }}
                  className="h-11 px-4 rounded-xl border-2 border-ink bg-white font-bold text-sm">
                  Cancelar
                </button>
              )}
            </div>
          </form>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl mb-4">Productos ({products.length})</h2>
          <div className="space-y-3" data-testid="admin-product-list">
            {products.map((p) => (
              <div key={p.id} className="bg-white border-2 border-ink rounded-2xl p-3 flex items-center gap-4 shadow-neo-sm">
                <img src={p.image} alt="" className="h-14 w-14 rounded-xl object-cover border-2 border-ink shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{p.name}</p>
                  <p className="text-xs text-ink/60 font-semibold">
                    {formatGs(p.price)} · {CATEGORIES.find((c) => c.id === p.category)?.label || p.category}
                  </p>
                </div>
                <button onClick={() => toggleActive(p)} data-testid={`admin-toggle-active-${p.id}`}
                  className={`h-9 px-3 rounded-full border-2 border-ink font-bold text-xs ${p.active ? "bg-kgreen text-white" : "bg-ink/10 text-ink/50"}`}>
                  {p.active ? "Activo" : "Oculto"}
                </button>
                <button onClick={() => { setEditing(p.id); setForm({ name: p.name, price: String(p.price), category: p.category, image: p.image, tag: p.tag || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="h-9 w-9 grid place-items-center rounded-lg border-2 border-ink bg-white hover:bg-kyellow transition-colors" data-testid={`admin-edit-${p.id}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(p)}
                  className="h-9 w-9 grid place-items-center rounded-lg border-2 border-ink bg-white hover:bg-kpink hover:text-white transition-colors" data-testid={`admin-delete-${p.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl mb-4">Pedidos recibidos ({orders.length})</h2>
          {orders.length === 0 ? (
            <p className="text-ink/50 font-medium bg-white border-2 border-dashed border-ink/30 rounded-2xl p-8 text-center" data-testid="admin-orders-empty">
              Aún no hay pedidos. Cuando un cliente envíe el formulario, aparecerá acá.
            </p>
          ) : (
            <div className="space-y-3" data-testid="admin-orders-list">
              {orders.map((o) => (
                <div key={o.id} className="bg-white border-2 border-ink rounded-2xl p-4 shadow-neo-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-bold">{o.name} <span className="font-semibold text-ink/50 text-sm">· {o.phone} · {o.city || "—"}</span></p>
                    <span className="font-display font-bold text-lg">{formatGs(o.total)}</span>
                  </div>
                  <p className="text-sm text-ink/60 font-medium mt-1">{o.delivery}</p>
                  <ul className="text-sm mt-2 space-y-0.5">
                    {o.items.map((i, idx) => (
                      <li key={idx} className="font-medium">• {i.name} x{i.qty}</li>
                    ))}
                  </ul>
                  {o.notes && <p className="text-sm text-ink/60 italic mt-2">"{o.notes}"</p>}
                  <p className="text-xs text-ink/40 font-mono mt-2">{new Date(o.created_at).toLocaleString("es-PY")}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
